import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar.tsx';
import { Toaster } from 'sonner';
import ESidebar from '@/components/everridge/sidebar/ESidebar';
import EHeader from '../everridge/header/EHeader';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import { useAuthUser } from '@/features/identity/hooks/useAuthUser';
import { useDefaultTenantHotel } from '@/features/hotels/hooks/useDefaultTenantHotel';
import { useHotelStorage } from '@/features/hotels/hooks/useHotelStorage';
import EToast from '../everridge/EToast';
import { ColorsHelper } from '@/utils/ColorsHelper';
import { Roles } from '@/@types/auth.types';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { useNavigate } from 'react-router-dom';
import { useSignOut } from '@/features/identity/hooks/useSignOut';
import { ApiConfig } from '@/config/api.config';
import { useMultiCurrencyStorage } from '@/features/hotels/hooks/useHotelMultiCurrencyStorage';

type Props = {};

const MainLayout: FC<PropsWithChildren<Props>> = ({ children }) => {
    const [isHotelSet, setIsHotelSet] = useState(false);

    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { tenant } = useAuthUser();
    const { getDefaultHotel } = useDefaultTenantHotel();
    const { setSelectedHotel } = useHotelStorage();
    const userRole = useUserRole();
    const navigate = useNavigate();
    const signOut = useSignOut();
    const { setMultiCurrency, setOrdersEnabled, setCurrencies } = useMultiCurrencyStorage();

    useEffect(() => {
        if (userRole !== Roles.superAdmin && tenant) {
            getDefaultHotel()
                .then((hotel) => {
                    if (hotel) {
                        setSelectedHotel(hotel);
                        setMultiCurrency(hotel.multiCurrency);
                        setOrdersEnabled(hotel.ordersEnabled ?? true);
                        setCurrencies({
                            calendarCurrency: hotel.calendarCurrency,
                            financePropertyCurrency: hotel.financePropertyCurrency,
                            financeServicesCurrency: hotel.financeServicesCurrency,
                            financeOtherCurrency: hotel.financeOtherCurrency,
                        });
                        ColorsHelper.setNewColor(hotel.primaryColor, hotel.darkMode);
                    } else {
                        EToast({
                            variant: 'error',
                            message: 'No default hotel found',
                            description:
                                'No default hotel found for the selected tenant. Please select a hotel manually.',
                        });
                    }
                })
                .finally(() => {
                    setIsHotelSet(true);
                });
        }

        if (!tenant) {
            signOut();
            navigate(ApiConfig.unAuthenticatedEntryPath, {
                replace: true,
            });
        }
    }, [tenant, userRole]);

    if (!isHotelSet && userRole !== Roles.superAdmin) return null;

    return (
        <>
            <SidebarProvider>
                <ESidebar />
                <SidebarInset>
                    <div className="relative max-w-screen h-screen overflow-hidden flex flex-col bg-popover-30">
                        <EHeader />
                        <div className="flex-1 flex flex-col overflow-y-scroll no-scrollbar">
                            <div
                                className={cn(
                                    'flex-1 flex flex-col gap-4',
                                    isMobile ? 'p-4' : 'p-8',
                                )}
                            >
                                {children}
                            </div>
                        </div>
                        <Toaster />
                    </div>
                </SidebarInset>
            </SidebarProvider>
        </>
    );
};

export default MainLayout;
